/**
 * Combined Health Score thresholds (vendor_config.health_score_thresholds).
 * Defaults match product v3 bands used across Birds Eye / My Accounts.
 */

export const HEALTH_SCORE_THRESHOLDS_KEY = "health_score_thresholds";

export type BusinessHealthThresholds = {
  /** Score ≥ strong_above → Strong */
  strong_above: number;
  /** Score ≥ stable_min and < strong_above → Stable */
  stable_min: number;
  /** Score ≥ concern_min and < stable_min → Concern */
  concern_min: number;
};

export type AccountHealthThresholds = {
  /** Score ≥ expand_min → Expand */
  expand_min: number;
  /** Score ≥ delegate_min and < expand_min → Monitor */
  delegate_min: number;
  /** Score ≥ defend_min and < delegate_min → Defend */
  defend_min: number;
  /** Score ≥ triage_min and < defend_min → Triage */
  triage_min: number;
};

export type HealthScoreThresholds = {
  business_health: BusinessHealthThresholds;
  account_health: AccountHealthThresholds;
};

export const DEFAULT_BUSINESS_HEALTH_THRESHOLDS: BusinessHealthThresholds = {
  strong_above: 70,
  stable_min: 50,
  concern_min: 1,
};

export const DEFAULT_ACCOUNT_HEALTH_THRESHOLDS: AccountHealthThresholds = {
  expand_min: 85,
  delegate_min: 70,
  defend_min: 55,
  triage_min: 1,
};

export const DEFAULT_HEALTH_SCORE_THRESHOLDS: HealthScoreThresholds = {
  business_health: DEFAULT_BUSINESS_HEALTH_THRESHOLDS,
  account_health: DEFAULT_ACCOUNT_HEALTH_THRESHOLDS,
};

const clampInt = (value: unknown, fallback: number, min = 0, max = 100) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
};

export const normalizeBusinessHealthThresholds = (
  raw?: Partial<BusinessHealthThresholds> | null
): BusinessHealthThresholds => {
  const base = DEFAULT_BUSINESS_HEALTH_THRESHOLDS;
  const concern_min = clampInt(raw?.concern_min, base.concern_min, 0, 98);
  let stable_min = clampInt(raw?.stable_min, base.stable_min, 0, 99);
  let strong_above = clampInt(raw?.strong_above, base.strong_above, 0, 100);
  stable_min = Math.max(concern_min + 1, stable_min);
  // Keep Stable non-empty: Strong starts at strong_above (inclusive).
  strong_above = Math.max(stable_min + 1, Math.min(100, strong_above));
  return { strong_above, stable_min, concern_min };
};

export const normalizeAccountHealthThresholds = (
  raw?: Partial<AccountHealthThresholds> | null
): AccountHealthThresholds => {
  const base = DEFAULT_ACCOUNT_HEALTH_THRESHOLDS;
  const triage_min = clampInt(raw?.triage_min, base.triage_min, 0, 97);
  let defend_min = clampInt(raw?.defend_min, base.defend_min, 0, 98);
  let delegate_min = clampInt(raw?.delegate_min, base.delegate_min, 0, 99);
  let expand_min = clampInt(raw?.expand_min, base.expand_min, 0, 100);
  defend_min = Math.max(triage_min, defend_min);
  delegate_min = Math.max(defend_min, delegate_min);
  expand_min = Math.max(delegate_min, expand_min);
  return { expand_min, delegate_min, defend_min, triage_min };
};

export const normalizeHealthScoreThresholds = (
  raw?: Partial<HealthScoreThresholds> | Record<string, unknown> | null
): HealthScoreThresholds => {
  const src =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const bhRaw =
    src.business_health && typeof src.business_health === "object"
      ? (src.business_health as Partial<BusinessHealthThresholds>)
      : undefined;
  const ahRaw =
    src.account_health && typeof src.account_health === "object"
      ? (src.account_health as Partial<AccountHealthThresholds>)
      : undefined;
  return {
    business_health: normalizeBusinessHealthThresholds(bhRaw),
    account_health: normalizeAccountHealthThresholds(ahRaw),
  };
};

export const extractVendorConfigValue = <T extends object>(
  payload: unknown
): Partial<T> | null | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  const record = payload as Record<string, unknown>;
  if (record.value && typeof record.value === "object") {
    return record.value as Partial<T>;
  }
  return record as Partial<T>;
};

/** Concern upper = stable_min - 1; Stable upper = strong_above - 1; Strong = ≥ strong_above */
export const formatBusinessHealthBandLegend = (
  t: BusinessHealthThresholds
): { concern: string; stable: string; strong: string } => {
  const concernHi = Math.max(t.concern_min, t.stable_min - 1);
  const stableHi = Math.max(t.stable_min, t.strong_above - 1);
  return {
    concern: `Concern ${t.concern_min}–${concernHi}`,
    stable: `Stable ${t.stable_min}–${stableHi}`,
    strong: `Strong ≥${t.strong_above}`,
  };
};

export const formatAccountHealthBandLegend = (
  t: AccountHealthThresholds
): {
  triage: string;
  defend: string;
  delegate: string;
  expand: string;
} => {
  const triageHi = Math.max(t.triage_min, t.defend_min - 1);
  const defendHi = Math.max(t.defend_min, t.delegate_min - 1);
  const delegateHi = Math.max(t.delegate_min, t.expand_min - 1);
  return {
    triage: `Triage ${t.triage_min}–${triageHi}`,
    defend: `Defend ${t.defend_min}–${defendHi}`,
    delegate: `Monitor ${t.delegate_min}–${delegateHi}`,
    expand: `Expand ≥${t.expand_min}`,
  };
};

/** Hex colors for KPI/matrix bubbles aligned with Business Health Settings bands. */
export const businessHealthBandHex = (
  score: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string => {
  const band = normalizeBusinessHealthThresholds(thresholds);
  if (!Number.isFinite(score) || score < band.concern_min) return "#94a3b8";
  if (score >= band.strong_above) return "#10b981";
  if (score >= band.stable_min) return "#f59e0b";
  return "#ef4444";
};

export const businessHealthBandTextClass = (
  score: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string => {
  const band = normalizeBusinessHealthThresholds(thresholds);
  if (!Number.isFinite(score) || score < band.concern_min)
    return "text-slate-500";
  if (score >= band.strong_above) return "text-green-600";
  if (score >= band.stable_min) return "text-yellow-600";
  return "text-red-600";
};

export const businessHealthBandBgClass = (
  score: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string => {
  const band = normalizeBusinessHealthThresholds(thresholds);
  if (!Number.isFinite(score) || score < band.concern_min)
    return "bg-slate-500";
  if (score >= band.strong_above) return "bg-green-600";
  if (score >= band.stable_min) return "bg-yellow-600";
  return "bg-red-600";
};

export type HealthScoreFieldErrors = {
  business_health: Partial<Record<keyof BusinessHealthThresholds, string>>;
  account_health: Partial<Record<keyof AccountHealthThresholds, string>>;
};

const isValidScore = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  Number.isInteger(value);

const rangeError = (label: string) =>
  `${label} must be an integer from 0 to 100`;

export const validateHealthScoreThresholds = (
  settings: HealthScoreThresholds
): { valid: boolean; errors: HealthScoreFieldErrors; messages: string[] } => {
  const errors: HealthScoreFieldErrors = {
    business_health: {},
    account_health: {},
  };
  const messages: string[] = [];
  const bh = settings.business_health;
  const ah = settings.account_health;

  const checkRange = (
    section: "business_health" | "account_health",
    key: string,
    value: unknown,
    label: string
  ) => {
    if (!isValidScore(value) || value < 0 || value > 100) {
      (errors[section] as Record<string, string>)[key] = rangeError(label);
      return false;
    }
    return true;
  };

  const bhOk =
    checkRange(
      "business_health",
      "concern_min",
      bh.concern_min,
      "Concern min"
    ) &&
    checkRange("business_health", "stable_min", bh.stable_min, "Stable min") &&
    checkRange(
      "business_health",
      "strong_above",
      bh.strong_above,
      "Strong above"
    );

  if (bhOk) {
    if (!(bh.concern_min < bh.stable_min)) {
      errors.business_health.concern_min =
        "Must be less than Stable min so Concern has a range";
      errors.business_health.stable_min = "Must be greater than Concern min";
    }
    if (!(bh.stable_min < bh.strong_above)) {
      errors.business_health.stable_min =
        "Must be less than Strong above so Stable has a range";
      errors.business_health.strong_above = "Must be greater than Stable min";
    }
  }

  const ahOk =
    checkRange("account_health", "triage_min", ah.triage_min, "Triage min") &&
    checkRange("account_health", "defend_min", ah.defend_min, "Defend min") &&
    checkRange(
      "account_health",
      "delegate_min",
      ah.delegate_min,
      "Monitor min"
    ) &&
    checkRange("account_health", "expand_min", ah.expand_min, "Expand min");

  if (ahOk) {
    if (!(ah.triage_min < ah.defend_min)) {
      errors.account_health.triage_min =
        "Must be less than Defend min so Triage has a range";
      errors.account_health.defend_min = "Must be greater than Triage min";
    }
    if (!(ah.defend_min < ah.delegate_min)) {
      errors.account_health.defend_min =
        "Must be less than Monitor min so Defend has a range";
      errors.account_health.delegate_min = "Must be greater than Defend min";
    }
    if (!(ah.delegate_min < ah.expand_min)) {
      errors.account_health.delegate_min =
        "Must be less than Expand min so Monitor has a range";
      errors.account_health.expand_min = "Must be greater than Monitor min";
    }
  }

  if (Object.keys(errors.business_health).length > 0) {
    messages.push(
      "Business Health bands must satisfy Concern min < Stable min < Strong above"
    );
  }
  if (Object.keys(errors.account_health).length > 0) {
    messages.push(
      "Account Health bands must satisfy Triage min < Defend min < Monitor min < Expand min"
    );
  }

  return {
    valid: messages.length === 0,
    errors,
    messages,
  };
};
